import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { type PaymentType as PayableType } from "@/api-v3";

import { type MethodProviderName } from "../types/available-payment-methods";

export type ServerFieldErrors = Record<string, Record<string, string>>;

interface PaymentMethodFormState {
  formData: Record<string, Record<string, string>>;
  serverFieldErrors: ServerFieldErrors;
  selectedPaymentMethod: MethodProviderName | undefined;
  returnTo: string | null;
  /**
   * The Payable Item kind the participant is about to claim, set when a Claim
   * Detour starts because the Default Payment Account cannot receive it. It
   * rides the same lifecycle as `returnTo`, so the payment methods list can
   * only ever partition on a kind that still has somewhere to return to.
   */
  requiredPayableType: PayableType | null;
}

const EMPTY_ERRORS: ServerFieldErrors = {};

const initialState: PaymentMethodFormState = {
  formData: {},
  serverFieldErrors: {},
  selectedPaymentMethod: undefined,
  returnTo: null,
  requiredPayableType: null,
};

export const paymentMethodFormSlice = createSlice({
  name: "paymentMethodForm",
  initialState,
  selectors: {
    selectPaymentMethodFormData: (state) => state.formData,
    // Rehydrated state from before this field existed has no value for it.
    selectServerFieldErrors: (state) => state.serverFieldErrors ?? EMPTY_ERRORS,
    selectSelectedPaymentMethod: (state) => state.selectedPaymentMethod,
    selectReturnTo: (state) => state.returnTo,
    // Rehydrated state from before this field existed has no value for it.
    selectRequiredPayableType: (state) => state.requiredPayableType ?? null,
  },
  reducers: {
    updateFormData: (
      state,
      action: PayloadAction<{
        formName: string;
        data: Record<string, string>;
      }>
    ) => {
      const { formName, data } = action.payload;
      const previousFormData = state.formData[formName] || {};
      state.formData = {
        ...state.formData,
        [formName]: {
          ...previousFormData,
          ...data,
        },
      };
    },
    setServerFieldErrors: (state, action: PayloadAction<ServerFieldErrors>) => {
      state.serverFieldErrors = action.payload;
    },
    clearServerFieldError: (
      state,
      action: PayloadAction<{ formName: string; fieldName: string }>
    ) => {
      const { formName, fieldName } = action.payload;
      const formErrors = state.serverFieldErrors?.[formName];
      if (!formErrors || !(fieldName in formErrors)) {
        return;
      }

      delete formErrors[fieldName];
      if (Object.keys(formErrors).length === 0) {
        delete state.serverFieldErrors[formName];
      }
    },
    clearServerFieldErrors: (state) => {
      state.serverFieldErrors = {};
    },
    setSelectedPaymentMethod: (
      state,
      action: PayloadAction<MethodProviderName>
    ) => {
      state.selectedPaymentMethod = action.payload;
    },
    setReturnTo: (state, action: PayloadAction<string>) => {
      state.returnTo = action.payload;
      // A plain return route is a detour with no kind to satisfy, so an earlier
      // detour's kind must not survive into it.
      state.requiredPayableType = null;
    },
    /**
     * Starts a Claim Detour: the route to come back to and the Payable Item kind
     * the chosen Payment Account must be able to receive.
     */
    startClaimDetour: (
      state,
      action: PayloadAction<{
        returnTo: string;
        requiredPayableType: PayableType;
      }>
    ) => {
      state.returnTo = action.payload.returnTo;
      state.requiredPayableType = action.payload.requiredPayableType;
    },
    clearReturnTo: (state) => {
      state.returnTo = null;
      // Cleared together so the list can never partition on a kind whose detour
      // has already ended.
      state.requiredPayableType = null;
    },
    resetForm: (state) => {
      state.formData = {};
      state.serverFieldErrors = {};
      state.selectedPaymentMethod = undefined;
    },
  },
});

export const {
  selectPaymentMethodFormData,
  selectServerFieldErrors,
  selectSelectedPaymentMethod,
  selectReturnTo,
  selectRequiredPayableType,
} = paymentMethodFormSlice.selectors;

export const {
  updateFormData,
  setServerFieldErrors,
  clearServerFieldError,
  clearServerFieldErrors,
  setSelectedPaymentMethod,
  setReturnTo,
  startClaimDetour,
  clearReturnTo,
  resetForm,
} = paymentMethodFormSlice.actions;
